/**
 * Accessibility Features Configuration
 * 
 * This module defines predefined accessibility features that can be configured
 * at the building level and overridden per room. The list is designed to be
 * easily extensible - simply add new entries to ACCESSIBILITY_FEATURES.
 * 
 * Icons use Lucide icon names for consistency with the rest of the application.
 */

export interface AccessibilityFeature {
  /** Unique identifier stored in the database */
  id: string
  /** Human-readable label for display */
  label: string
  /** Lucide icon name */
  icon: string
  /** Detailed description for tooltips/help text */
  description: string
  /** Category for grouping in UI */
  category: 'mobility' | 'sensory' | 'cognitive' | 'general'
}

/**
 * Predefined accessibility features
 * 
 * To add a new feature:
 * 1. Add a new entry to this array with a unique id
 * 2. The feature will automatically appear in building/room forms and marketplace filters
 */
export const ACCESSIBILITY_FEATURES: AccessibilityFeature[] = [
  // Mobility
  {
    id: 'wheelchair_accessible',
    label: 'Wheelchair Accessible',
    icon: 'Accessibility',
    description: 'The space is fully accessible for wheelchair users',
    category: 'mobility',
  },
  {
    id: 'step_free_access',
    label: 'Step-Free Access',
    icon: 'ArrowRightLeft',
    description: 'No steps or stairs to access the space',
    category: 'mobility',
  },
  {
    id: 'lift_access',
    label: 'Lift/Elevator Access',
    icon: 'ArrowUpDown',
    description: 'Accessible via lift or elevator',
    category: 'mobility',
  },
  {
    id: 'accessible_parking',
    label: 'Accessible Parking',
    icon: 'ParkingCircle',
    description: 'Designated accessible parking spaces available nearby',
    category: 'mobility',
  },
  {
    id: 'accessible_toilet',
    label: 'Accessible Toilet',
    icon: 'Bath',
    description: 'Accessible toilet facilities available',
    category: 'mobility',
  },
  {
    id: 'wide_doorways',
    label: 'Wide Doorways',
    icon: 'DoorOpen',
    description: 'Doorways are wide enough for wheelchair access (minimum 32 inches)',
    category: 'mobility',
  },
  {
    id: 'lowered_counters',
    label: 'Lowered Counters',
    icon: 'Table',
    description: 'Reception or service counters at accessible height',
    category: 'mobility',
  },
  
  // Sensory
  {
    id: 'hearing_loop',
    label: 'Hearing Loop',
    icon: 'Ear',
    description: 'Hearing/induction loop system installed',
    category: 'sensory',
  },
  {
    id: 'visual_aids',
    label: 'Visual Aids',
    icon: 'Eye',
    description: 'Visual aids and signage for those with hearing impairments',
    category: 'sensory',
  },
  {
    id: 'braille_signage',
    label: 'Braille Signage',
    icon: 'Braces',
    description: 'Braille signage throughout the building',
    category: 'sensory',
  },
  {
    id: 'good_lighting',
    label: 'Good Lighting',
    icon: 'Sun',
    description: 'Well-lit spaces with adjustable lighting options',
    category: 'sensory',
  },
  {
    id: 'audio_description',
    label: 'Audio Description Available',
    icon: 'Volume2',
    description: 'Audio description services available on request',
    category: 'sensory',
  },
  
  // Cognitive
  {
    id: 'quiet_space',
    label: 'Quiet Space',
    icon: 'VolumeX',
    description: 'Low sensory environment with minimal noise',
    category: 'cognitive',
  },
  {
    id: 'clear_signage',
    label: 'Clear Signage',
    icon: 'Signpost',
    description: 'Clear, simple signage with symbols/pictures',
    category: 'cognitive',
  },
  
  // General
  {
    id: 'assistance_dogs',
    label: 'Assistance Dogs Welcome',
    icon: 'Dog',
    description: 'Assistance/service dogs are welcome',
    category: 'general',
  },
  {
    id: 'staff_assistance',
    label: 'Staff Assistance Available',
    icon: 'HandHelping',
    description: 'Staff trained to assist visitors with accessibility needs',
    category: 'general',
  },
  {
    id: 'accessible_entrance',
    label: 'Accessible Entrance',
    icon: 'DoorClosed',
    description: 'Main entrance is accessible with automatic or easy-open doors',
    category: 'general',
  },
]

/**
 * Get an accessibility feature by its ID
 */
export function getAccessibilityFeature(id: string): AccessibilityFeature | undefined {
  return ACCESSIBILITY_FEATURES.find(f => f.id === id)
}

/**
 * Get all accessibility features for a category
 */
export function getAccessibilityFeaturesByCategory(category: AccessibilityFeature['category']): AccessibilityFeature[] {
  return ACCESSIBILITY_FEATURES.filter(f => f.category === category)
}

/**
 * Get all unique categories
 */
export function getAccessibilityCategories(): AccessibilityFeature['category'][] {
  return ['mobility', 'sensory', 'cognitive', 'general']
}

/**
 * Category labels for display
 */
export const ACCESSIBILITY_CATEGORY_LABELS: Record<AccessibilityFeature['category'], string> = {
  mobility: 'Mobility',
  sensory: 'Sensory',
  cognitive: 'Cognitive',
  general: 'General',
}

/**
 * Validate that all provided feature IDs are valid
 */
export function validateAccessibilityFeatures(featureIds: string[]): { valid: boolean; invalidIds: string[] } {
  const validIds = new Set(ACCESSIBILITY_FEATURES.map(f => f.id))
  const invalidIds = featureIds.filter(id => !validIds.has(id))
  return {
    valid: invalidIds.length === 0,
    invalidIds,
  }
}

/**
 * Get feature labels for a list of feature IDs
 */
export function getAccessibilityLabels(featureIds: string[]): string[] {
  return featureIds
    .map(id => getAccessibilityFeature(id)?.label)
    .filter((label): label is string => label !== undefined)
}
